"use client"

import { useState, useContext, useEffect, useMemo } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import { formatInputWithSeparators, parseLocalizedNumber, getLocaleConfig } from "../../utils/numeros.js"
import {
  TextField,
  Button,
  Typography,
  Box,
  MenuItem,
  FormControl,
  Select,
  Autocomplete,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Chip,
  Alert,
  Fade,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material"
import CrearEntidadClienteModalComponent from "../adminComponent/CrearEntidadClienteModalComponent.js"
import FileUploadDragDrop from "./FileUploadDragDrop.js"

import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import tz from "dayjs/plugin/timezone"
import {
  ReceiptLong as ReceiptLongIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  MonetizationOn as MonetizationOnIcon,
  Payment as PaymentIcon,
  Description as DescriptionIcon,
  Save as SaveIcon,
  LocationOn as LocationOnIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material"

dayjs.extend(utc)
dayjs.extend(tz)

const GestionGastosSimple = () => {
  const {
    monedas,
    getMoneda,
    tipoMedioDePago,
    getTipoMedioDePago,
    subCategoriaTransaccion,
    getSubCategoriaTransaccion,
    postTransaccion,
    getMedioDePagoIdTipoEspecifico,
    medioDePagoIdTipoEspecifico,
    entidadesClientesTresLetras,
    getEntidadesClientesTresLetras,
    postCrearPago,
    updateCuentaCorriente,
    postTransaccionPago,
    categoriaTransaccion,
    getCategoriaTransaccion,
    user,
    getSubCategoriaTransaccionByCategoria,
    postCuentaCorriente,
    getCuentaCorrienteByIdEntidad,
    getUbicacion,
    ubicaciones,
    getTipoEntidad,
    tipoEntidades,
    formatoNumericoGlobal,
    getParametrosGlobales,
    } = useContext(ErpContext)

  // Estados principales
  const [formData, setFormData] = useState({
    tipoEntidad: null,
    entidad: null,
    ubicacion: null,
    moneda: 1, // Peso argentino por defecto
    subcategoria: "",
    tipoMedioPago: null,
    medioPago: null,
    monto: "", // Valor numérico interno (number o "")
    descripcion: "",
    aplicarCuentaCorriente: false,
  })
  const [archivosAdjuntos, setArchivosAdjuntos] = useState([])

  const [montoDisplay, setMontoDisplay] = useState("") // Valor formateado para mostrar
  const [searchTextCliente, setSearchTextCliente] = useState("")
  const [tiposEntidadFiltrados, setTiposEntidadFiltrados] = useState([])
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState("")
  const [loading, setLoading] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState("Cargando...")
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [ocultarCheckboxCC, setOcultarCheckboxCC] = useState(false)
  const [tildeCheckboxCC, setTildeCheckboxCC] = useState(false)

  // Obtener configuración de locale (memoizado para evitar recálculos)
  const localeConfig = useMemo(
    () => getLocaleConfig(formatoNumericoGlobal || null),
    [formatoNumericoGlobal]
  )

  // Estados para creación/edición de entidades (proveedor)
  const [abrirModalCrearEntidad, setAbrirModalCrearEntidad] = useState(false)
  const [entidadAEditar, setEntidadAEditar] = useState(null)
  const [isEditing, setIsEditing] = useState(false)

  const today = dayjs()
  const [startDate, setStartDate] = useState(today)


  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    setLoadingMessage("Cargando datos iniciales...")
    try {
      await getMoneda()
      await getTipoMedioDePago()
      await getSubCategoriaTransaccion()
      await getCategoriaTransaccion()
      await getUbicacion()
      await getTipoEntidad()
      
      // Cargar parámetros globales para checkbox de cuenta corriente
      const parametros = await getParametrosGlobales(["verCheckboxCrearGastoAfectaCuentaCorriente", "valorCheckboxCrearGastoAfectaCuentaCorriente"])
      const ocultarCC = parametros?.verCheckboxCrearGastoAfectaCuentaCorriente === "0" || parametros?.verCheckboxCrearGastoAfectaCuentaCorriente === 0
      setOcultarCheckboxCC(ocultarCC)
      
      // Cargar parámetro para tildar checkbox por defecto
      const tildarCC = parametros?.valorCheckboxCrearGastoAfectaCuentaCorriente === "1" || parametros?.valorCheckboxCrearGastoAfectaCuentaCorriente === 1
      setTildeCheckboxCC(tildarCC)
      
      // Si el parámetro indica que debe estar tildado, actualizar el estado del formulario
      if (tildarCC) {
        setFormData(prev => ({ ...prev, aplicarCuentaCorriente: true }))
      }
    } catch (err) {
      console.error("Error al cargar datos:", err)
      setError("No se pudieron cargar los datos. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (tipoEntidades.length > 0) {
      const filtrados = tipoEntidades.filter(tipo => tipo.verEnGasto === true)
      setTiposEntidadFiltrados(filtrados)
    }
  }, [tipoEntidades])

  // Preseleccionar "Proveedor" (id = 2) por defecto
  useEffect(() => {
    if (tipoEntidades.length > 0 && !formData.tipoEntidad) {
      const proveedor = tipoEntidades.find(tipo => tipo.id === 2)
      if (proveedor && proveedor.verEnGasto === true) {
        setFormData(prev => ({ ...prev, tipoEntidad: proveedor }))
      }
    }
  }, [tipoEntidades])

  // Preseleccionar ubicación con id = 1 por defecto
  useEffect(() => {
    if (ubicaciones.length > 0 && !formData.ubicacion) {
      const ubicacionDefault = ubicaciones.find(ubic => ubic.id === 1)
      if (ubicacionDefault) {
        setFormData(prev => ({ ...prev, ubicacion: ubicacionDefault }))
      }
    }
  }, [ubicaciones])

  useEffect(() => {
    if (formData.tipoMedioPago?.id) {
      getMedioDePagoIdTipoEspecifico(formData.tipoMedioPago.id)
    }
  }, [formData.tipoMedioPago])

  useEffect(() => {
    if (searchTextCliente.length >= 3 && formData.tipoEntidad) {
      getEntidadesClientesTresLetras(searchTextCliente, [formData.tipoEntidad.id], false, false)
    }
  }, [searchTextCliente, formData.tipoEntidad])

  // Sincronizar montoDisplay con formData.monto cuando cambia desde fuera
  useEffect(() => {
    if (formData.monto === "" || formData.monto == null) {
      setMontoDisplay("")
      return
    }

    const locale = localeConfig.locale
    const decimalSeparator = localeConfig.decimalSeparator

    if (typeof formData.monto === "number") {
      let formatted = formatInputWithSeparators(formData.monto, locale)
      setMontoDisplay(formatted)
    } else if (typeof formData.monto === "string") {
      const parsed = parseLocalizedNumber(formData.monto, locale)
      if (parsed !== null) {
        let formatted = formatInputWithSeparators(parsed, locale)
        if (formData.monto.endsWith(decimalSeparator) && !formatted.endsWith(decimalSeparator)) {
          formatted += decimalSeparator
        }
        setMontoDisplay(formatted)
      }
    }
  }, [formData.monto, localeConfig])

  const handleInputChange = async (field, value) => {
    // 1) Actualizo el campo base inmediatamente
    setFormData((prev) => ({ ...prev, [field]: value }))

    // 🔒 NUEVO: Si cambia el checkbox "Afecta Cuenta Corriente"
    if (field === "aplicarCuentaCorriente") {
      if (value === true) {
        // Buscar tipo medio de pago "Cuenta Corriente" (debe existir en la BD)
        const tipoCuentaCorriente = tipoMedioDePago.find(
          (tipo) => tipo.descripcion === "Cuenta Corriente" || tipo.afectaCuentaCorriente === true
        )
        
        if (tipoCuentaCorriente) {
          // Buscar medio de pago "Cuenta Corriente" - usar el valor RETORNADO (no el estado)
          const mediosDePago = await getMedioDePagoIdTipoEspecifico(tipoCuentaCorriente.id)
          const medioCuentaCorriente = mediosDePago?.find(
            (medio) => medio.descripcion === "Cuenta Corriente"
          )
          
          // Bloquear medios de pago a "Cuenta Corriente"
          setFormData((prev) => ({
            ...prev,
            aplicarCuentaCorriente: value,
            tipoMedioPago: tipoCuentaCorriente,
            medioPago: medioCuentaCorriente || null,
          }))
        }
      } else {
        // Si se desmarca, limpiar medios de pago
        setFormData((prev) => ({
          ...prev,
          aplicarCuentaCorriente: value,
          tipoMedioPago: null,
          medioPago: null,
        }))
      }
      return // Salir temprano para evitar procesamiento adicional
    }

    // 2) Si cambiaron subcategoría
    if (field === "subcategoria") {
      setLoading(true)
      setLoadingMessage("Buscando datos de la subcategoría...")
      try {
      // Solo auto-popular si entidad es null o vacía
      if (!formData.entidad || !formData.entidad.id) {
        const popular = await getSubCategoriaTransaccionByCategoria(value, null)
        // console.log("Popular por subcategoría:", popular)
          if (!popular) {
            setLoading(false)
            return
          }

        // arma el objeto igualito a los options:
        const entidadOption = {
          label: `${popular.entidad.descripcion} - ${popular.entidad.apellido} - ${popular.entidad.dniCuitCuil}`,
          descripcion: popular.entidad.descripcion,
          apellido: popular.entidad.apellido,
          dniCuitCuil: popular.entidad.dniCuitCuil,
          id: popular.entidad.id,
        }

        setFormData((prev) => ({
          ...prev,
          entidad: entidadOption,
          monto: popular.montoTotal,
          descripcion: popular.descripcion,
          tipoMedioPago: popular.transaccionPago[0]?.pago.medioDePago.tipoMedioDePago || null,
          medioPago: popular.transaccionPago[0]?.pago.medioDePago || null,
        }))
      } else {
        // Si entidad ya está seleccionada, buscar transacción con entidad + subcategoría
        const popular = await getSubCategoriaTransaccionByCategoria(value, formData.entidad.id)
        // console.log("Popular por entidad + subcategoría:", popular)

        if (popular) {
          // Si encuentra transacción, actualizar datos
          setFormData((prev) => ({
            ...prev,
            monto: popular.montoTotal,
            descripcion: popular.descripcion,
            tipoMedioPago: popular.transaccionPago[0]?.pago.medioDePago.tipoMedioDePago || null,
            medioPago: popular.transaccionPago[0]?.pago.medioDePago || null,
          }))
        } else {
          // Si no encuentra nada, limpiar campos restantes
          setFormData((prev) => ({
            ...prev,
            monto: "",
            descripcion: "",
            tipoMedioPago: null,
            medioPago: null,
          }))
        }
        }
      } catch (error) {
        console.error("Error al buscar datos de subcategoría:", error)
      } finally {
        setLoading(false)
      }
    }

    // 3) Si cambiaron entidad
    if (field === "entidad") {
      // Solo auto-popular si tanto entidad como subcategoría eran null/vacíos
      if ((!formData.entidad || !formData.entidad.id) && (!formData.subcategoria || formData.subcategoria === "")) {
        if (!value?.id) return

        setLoading(true)
        setLoadingMessage("Buscando datos de la entidad...")
        try {
        const popular = await getSubCategoriaTransaccionByCategoria(null, value.id)
        // console.log("Popular por entidad:", popular)

          if (!popular) {
            setLoading(false)
            return
          }

        // Selecciono subcategoría y chip
        setFormData((prev) => ({ ...prev, subcategoria: popular.idCategorizacion }))
        const subCatObj = subCategoriaTransaccion.find((s) => s.id === popular.idCategorizacion)
        setCategoriaSeleccionada(subCatObj?.descripcion || "")

        // Actualizo monto, descripción y pagos
        setFormData((prev) => ({
          ...prev,
          monto: popular.montoTotal,
          descripcion: popular.descripcion,
          tipoMedioPago: popular.transaccionPago[0]?.pago.medioDePago.tipoMedioDePago || null,
          medioPago: popular.transaccionPago[0]?.pago.medioDePago || null,
        }))
        } catch (error) {
          console.error("Error al buscar datos de entidad:", error)
        } finally {
          setLoading(false)
        }
      }
    }

    // 4) Si cambiaron tipo de entidad
    if (field === "tipoEntidad") {
      // Limpiar entidad seleccionada al cambiar tipo
      setFormData((prev) => ({ ...prev, entidad: null }))
      setSearchTextCliente("")
    }
  }

  // Abrir modal para crear proveedor
  const abrirCrearEntidad = () => {
    setEntidadAEditar(null)
    setIsEditing(false)
    setAbrirModalCrearEntidad(true)
  }

  // Cerrar modal crear/editar entidad
  const cerrarCrearEntidad = () => {
    setAbrirModalCrearEntidad(false)
  }

  // Callback cuando se crea la entidad desde el modal
  const handleEntidadCreada = (entidad) => {
    const nuevaEntidadOption = {
      label: `${entidad.descripcion} - ${entidad.apellido} - ${entidad.dniCuitCuil}`,
      descripcion: entidad.descripcion,
      apellido: entidad.apellido,
      dniCuitCuil: entidad.dniCuitCuil,
      id: entidad.id,
      idTipoEntidad: entidad.idTipoEntidad,
    }

    setFormData((prev) => ({ ...prev, entidad: nuevaEntidadOption }))

    const tipoEntidadId = entidad.idTipoEntidad || formData.tipoEntidad?.id
    const filtros = tipoEntidadId ? [tipoEntidadId] : undefined
    getEntidadesClientesTresLetras("", filtros, false, false)
  }

  // Manejo de alerta desde modal de entidad (opcional)
  const handleAlertaEntidad = () => {
    // No-op en gastos; se podría integrar un Snackbar si es necesario
  }

  const resetForm = () => {
    setFormData({
      tipoEntidad: null,
      entidad: null,
      moneda: 1,
      subcategoria: "",
      tipoMedioPago: null,
      medioPago: null,
      monto: "",
      descripcion: "",
      aplicarCuentaCorriente: tildeCheckboxCC, // Respetar el parámetro al resetear
    })
    setMontoDisplay("")
    setCategoriaSeleccionada("")
    setSearchTextCliente("")
    setArchivosAdjuntos([])
    setError(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validaciones básicas
    if (!formData.tipoEntidad?.id || !formData.entidad?.id || !formData.ubicacion?.id || !formData.monto || !formData.descripcion || !formData.subcategoria) {
      setError("Por favor complete todos los campos obligatorios")
      return
    }

    if (!formData.tipoMedioPago || !formData.medioPago) {
      setError("Por favor seleccione un medio de pago")
      return
    }

    setLoading(true)
    setLoadingMessage("Guardando gasto...")
    try {
      let descripcionFinal = formData.descripcion

      // Fecha: del DatePicker (usuario elige día). Hora: instante actual al guardar.
      // Así cada gasto tiene timestamp único aunque se creen varios seguidos.
      // Respetamos criterios de FECHAS-Y-TIMEZONE: zona del navegador, ISO 8601 con offset.
      const zona = Intl.DateTimeFormat().resolvedOptions().timeZone
      const fechaDia = dayjs(startDate).tz(zona)
      const ahora = dayjs().tz(zona)
      const fechaCombinada = fechaDia
        .hour(ahora.hour())
        .minute(ahora.minute())
        .second(ahora.second())
        .millisecond(ahora.millisecond())
      const fechaServidor = fechaCombinada.format()

      // 🔄 NUEVO: Determinar tipo de transacción según checkbox
      // Si "Afecta Cuenta Corriente" está marcado → Tipo 9 (Cuenta Corriente)
      // Si NO está marcado → Tipo 5 (Egreso)
      const idTipoTransaccion = formData.aplicarCuentaCorriente ? 9 : 5

      // Crear transacción
      const transaccionResponse = await postTransaccion(
        formData.monto,
        idTipoTransaccion, // 9 si afecta CC, 5 si es egreso normal
        formData.entidad.id,
        formData.subcategoria,
        descripcionFinal,
        user.id,
        formData.ubicacion.id, // ubicación seleccionada
        1, // negocio central
        0, // descuento
        0, // porcentaje descuento
        null,
        fechaServidor,
        formData.aplicarCuentaCorriente, // afecta cuenta corriente
        null, // clientOS
        null, // operacionParaCuentaCorriente (null para tipo 9, se usa operacionCuentaCorriente)
        archivosAdjuntos.length > 0 ? archivosAdjuntos : null // archivos adjuntos
      )

      const idTransaccion = transaccionResponse.data.id

      // Crear pago
      const transaccionCrearPago = await postCrearPago(
        formData.moneda,
        1, // cotización
        formData.medioPago.id,
        formData.monto,
      )

      const idTransaccionCrearPago = transaccionCrearPago.data.id
      await postTransaccionPago(idTransaccion, idTransaccionCrearPago)

      // Actualizar cuenta corriente si es necesario
      if (formData.aplicarCuentaCorriente) {
      // Manejar cuenta corriente si es necesario
      const cuentas = await getCuentaCorrienteByIdEntidad(formData.entidad.id);
      const cuentaCorrienteConId = cuentas.find(
        (ct) => ct.idEntidad === formData.entidad.id
      );
      if (cuentaCorrienteConId) {
        await updateCuentaCorriente(formData.entidad.id, formData.monto, 9);
      } else {
        await postCuentaCorriente(formData.entidad.id, formData.monto, 9);
      }
      
      }

      // Resetear formulario
      setFormData({
        tipoEntidad: null,
        entidad: null,
        ubicacion: null,
        moneda: 1,
        subcategoria: "",
        tipoMedioPago: null,
        medioPago: null,
        monto: "",
        descripcion: "",
        aplicarCuentaCorriente: tildeCheckboxCC, // Respetar el parámetro al resetear
      })
      setMontoDisplay("")
      setCategoriaSeleccionada("")
      setSearchTextCliente("")
      setArchivosAdjuntos([])

      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (error) {
      console.error("Error al crear transacción:", error)
      setError("No se pudo crear la transacción. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ p: 2, maxWidth: "100%", mx: "auto" }}>
      {/* Encabezado simplificado */}
      <Box sx={{ mb: 2, textAlign: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 0.5 }}>
          <ReceiptLongIcon sx={{ fontSize: 28, color: "rgb(244, 147, 14)", mr: 1 }} />
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: "#2c3e50" }}>
            Nuevo Gasto
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Complete los datos para registrar un nuevo gasto
        </Typography>
      </Box>

      <Card elevation={3} sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Tipo de Entidad - Más pequeño */}
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}>
                  <CategoryIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: 20 }} />
                  Tipo de Entidad
                </Typography>
                <Autocomplete
                  size="small"
                  options={tiposEntidadFiltrados}
                  getOptionLabel={(option) => option.descripcion || ""}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  value={formData.tipoEntidad}
                  onChange={(e, newValue) => handleInputChange("tipoEntidad", newValue)}
                  sx={{ width: "100%" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Seleccionar tipo..."
                      required
                      fullWidth
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <CategoryIcon color="action" fontSize="small" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Entidad - Más grande con checkbox al lado */}
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}>
                  <PersonIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: 20 }} />
                  Entidad
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Autocomplete
                    size="small"
                    options={entidadesClientesTresLetras.map((it) => ({
                      label: `${it.descripcion} - ${it.apellido} - ${it.dniCuitCuil}`,
                      descripcion: it.descripcion,
                      apellido: it.apellido,
                      dniCuitCuil: it.dniCuitCuil,
                      id: it.id,
                    }))}
                    getOptionLabel={(option) => option.label || ""}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    value={formData.entidad}
                    onChange={(e, newValue) => handleInputChange("entidad", newValue)}
                    onInputChange={(e, newInputValue) => setSearchTextCliente(newInputValue)}
                    disabled={!formData.tipoEntidad}
                    sx={{ flex: 1 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={formData.tipoEntidad ? "Buscar Entidad..." : "Primero seleccione tipo"}
                        required
                        fullWidth
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <PersonIcon color="action" fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <Button variant="outlined" size="small" onClick={abrirCrearEntidad} disabled={!formData.tipoEntidad}>
                    Crear {formData.tipoEntidad?.descripcion || "Entidad"}
                  </Button>
                  {!ocultarCheckboxCC && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={formData.aplicarCuentaCorriente}
                        onChange={(e) => handleInputChange("aplicarCuentaCorriente", e.target.checked)}
                      />
                    }
                    label="Afecta cuenta corriente"
                    sx={{ ml: 1 }}
                  />
                  )}
                </Box>
              </Grid>

              {/* Fecha - Esquina superior derecha */}
              <Grid item xs={12} md={4}>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={startDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      format="DD/MM/YYYY"
                      label="Fecha"
                      slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 } } }}
                      sx={{ width: "200px" }}
                    />
                  </LocalizationProvider>
                </Box>
              </Grid>

              {/* Ubicación */}
              <Grid item xs={12} md={8}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}>
                  <LocationOnIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: 20 }} />
                  Ubicación
                </Typography>
                <Autocomplete
                  size="small"
                  options={ubicaciones}
                  getOptionLabel={(option) => option.descripcion || ""}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  value={formData.ubicacion}
                  onChange={(e, newValue) => handleInputChange("ubicacion", newValue)}
                  sx={{ width: "70%" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Seleccionar ubicación..."
                      required
                      fullWidth
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <LocationOnIcon color="action" fontSize="small" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Categoría y Monto */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}>
                  <CategoryIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: 20 }} />
                  SubCategoría
                </Typography>
                <FormControl fullWidth required size="small">
                  <Select
                    value={formData.subcategoria}
                    onChange={(e) => handleInputChange("subcategoria", e.target.value)}
                    displayEmpty
                    size="small"
                    startAdornment={
                      <InputAdornment position="start">
                        <CategoryIcon color="action" fontSize="small" />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="" disabled>
                      Seleccionar SubCategoría
                    </MenuItem>
                    {subCategoriaTransaccion.map((subCat) => (
                      <MenuItem key={subCat.id} value={subCat.id}>
                        {subCat.descripcion}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {categoriaSeleccionada && (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip size="small" label={`Tipo: ${categoriaSeleccionada}`} color="primary" variant="outlined" />
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}>
                  <MonetizationOnIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: 20 }} />
                  Monto
                </Typography>
                <TextField
                  type="text"
                  placeholder="0.00"
                  variant="outlined"
                  fullWidth
                  required
                  size="small"
                  value={montoDisplay}
                  onChange={(e) => {
                    const inputValue = e.target.value
                    const { locale, decimalSeparator } = localeConfig

                    if (inputValue === "") {
                      setMontoDisplay("")
                      setFormData((prev) => ({ ...prev, monto: "" }))
                      return
                    }

                    const parsedValue = parseLocalizedNumber(inputValue, locale)

                    if (parsedValue === null && inputValue.trim() !== "") {
                      return // Keep previous valid input if current is unparseable
                    }

                    let formattedValue = parsedValue !== null
                      ? formatInputWithSeparators(parsedValue, locale)
                      : ""

                    const hasDecimalSeparator = inputValue.includes(decimalSeparator)
                    const endsWithDecimal = inputValue.endsWith(decimalSeparator)
                    const [, fractionalPartOriginal] = inputValue.split(decimalSeparator)

                    // Special handling for decimal separator at the end or during fractional input
                    if (endsWithDecimal && !formattedValue.endsWith(decimalSeparator)) {
                      formattedValue += decimalSeparator
                    } else if (hasDecimalSeparator && fractionalPartOriginal !== undefined) {
                      const cleanedFraction = fractionalPartOriginal.replace(/\D/g, "").slice(0, 6)
                      const integerPart = formattedValue.split(decimalSeparator)[0]
                      formattedValue = `${integerPart}${decimalSeparator}${cleanedFraction}`
                    }

                    setMontoDisplay(formattedValue)
                    setFormData((prev) => ({ ...prev, monto: parsedValue ?? "" }))

                    // Keep cursor at the end
                    requestAnimationFrame(() => {
                      const input = e.target
                      if (input) {
                        input.setSelectionRange(formattedValue.length, formattedValue.length)
                      }
                    })
                  }}
                  onBlur={(e) => {
                    const { locale } = localeConfig
                    const parsed = parseLocalizedNumber(e.target.value, locale)
                    if (parsed !== null && parsed > 0) {
                      const formatted = formatInputWithSeparators(parsed, locale)
                      setMontoDisplay(formatted)
                      setFormData((prev) => ({ ...prev, monto: parsed }))
                    } else if (e.target.value === "") {
                      setMontoDisplay("")
                      setFormData((prev) => ({ ...prev, monto: "" }))
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MonetizationOnIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    inputMode: "decimal",
                    autoComplete: "off",
                  }}
                />
              </Grid>

              {/* Medios de Pago */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}>
                  <PaymentIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: 20 }} />
                  Tipo de Pago
                </Typography>
                <Autocomplete
                  size="small"
                  options={tipoMedioDePago.filter((tipo) => {
                    // 🔒 NUEVO: Si checkbox NO está marcado, excluir tipos que afectan CC
                    if (!formData.aplicarCuentaCorriente) {
                      return tipo.afectaCuentaCorriente !== true
                    }
                    return true
                  })}
                  getOptionLabel={(option) => option.descripcion}
                  value={formData.tipoMedioPago}
                  onChange={(event, newValue) => {
                    handleInputChange("tipoMedioPago", newValue)
                    handleInputChange("medioPago", null)
                  }}
                  disabled={formData.aplicarCuentaCorriente} // 🔒 NUEVO: Bloquear si checkbox marcado
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Seleccionar tipo de pago..."
                      required
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <PaymentIcon color="action" fontSize="small" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}>
                  <PaymentIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: 20 }} />
                  Medio de Pago
                </Typography>
                <Autocomplete
                  size="small"
                  options={medioDePagoIdTipoEspecifico}
                  getOptionLabel={(option) => option.descripcion}
                  value={formData.medioPago}
                  onChange={(event, newValue) => handleInputChange("medioPago", newValue)}
                  disabled={!formData.tipoMedioPago || formData.aplicarCuentaCorriente} // 🔒 NUEVO: Bloquear si checkbox marcado
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Seleccionar medio de pago..."
                      required
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <PaymentIcon color="action" fontSize="small" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Descripción */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}>
                  <DescriptionIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: 20 }} />
                  Descripción
                </Typography>
                <TextField
                  placeholder="Describe el gasto..."
                  variant="outlined"
                  fullWidth
                  required
                  size="small"
                  multiline
                  rows={2}
                  value={formData.descripcion}
                  onChange={(e) => handleInputChange("descripcion", e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1 }}>
                        <DescriptionIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Archivos Adjuntos */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}>
                  <CloudUploadIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: 20 }} />
                  Archivos Adjuntos
                </Typography>
                <FileUploadDragDrop
                  files={archivosAdjuntos}
                  onFilesChange={setArchivosAdjuntos}
                  maxFiles={2}
                  maxTotalSizeMB={5}
                />
              </Grid>

              {/* Botones de acción */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={loading}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      py: 1,
                      px: 4,
                      fontSize: "1rem",
                      fontWeight: 600,
                      borderColor: "#5b5b5f",
                      color: "#5b5b5f",
                      "&:hover": {
                        borderColor: "#4a4a4e",
                        backgroundColor: "rgba(91, 91, 95, 0.04)",
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Resetear
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    startIcon={<SaveIcon />}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      py: 1,
                      px: 4,
                      fontSize: "1rem",
                      fontWeight: 600,
                      backgroundColor: "#5b5b5f",
                      "&:hover": {
                        backgroundColor: "#4a4a4e",
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {loading ? "Guardando..." : "Guardar Gasto"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Alertas */}
      <Fade in={success}>
        <Alert
          severity="success"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 9999,
            borderRadius: 2,
            boxShadow: 3,
          }}
          onClose={() => setSuccess(false)}
        >
          ¡Gasto registrado correctamente!
        </Alert>
      </Fade>

      {error && (
        <Alert
          severity="error"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 9999,
            borderRadius: 2,
            boxShadow: 3,
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              p: 3,
              borderRadius: 2,
              bgcolor: "background.paper",
            }}
          >
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6">{loadingMessage}</Typography>
          </Box>
        </Box>
      )}

      {/* Modal para crear/editar entidad (Proveedor) */}
      <CrearEntidadClienteModalComponent
        open={abrirModalCrearEntidad}
        handleClose={cerrarCrearEntidad}
        entidad={entidadAEditar}
        isEditing={isEditing}
        handleAlerta={handleAlertaEntidad}
        onClienteCreado={handleEntidadCreada}
        tipoEntidad={formData.tipoEntidad}
        tipoEntidadReadonly={false}
      />
    </Box>
  )
}

export default GestionGastosSimple
